import React, { useEffect, useReducer, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import openSocket from "../../services/socket-io";

import {
  Button,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";

import { DeleteOutline, Edit } from "@material-ui/icons";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

import BotQueriesModal from "../../components/BotQueriesModal";
import ConfirmationModal from "../../components/ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },

  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_BOTS": {
      const botsquerry = action.payload;
      const newBots = [];

      botsquerry.forEach((bot) => {
        const botIndex = state.findIndex((q) => q.id === bot.id);

        if (botIndex !== -1) {
          state[botIndex] = bot;
        } else {
          newBots.push(bot);
        }
      });

      return [...state, ...newBots];
    }

    case "UPDATE_BOTS": {
      const botsIndex = state.findIndex(
        (u) => u.id === action.payload.id
      );

      if (botsIndex !== -1) {
        state[botsIndex] = action.payload;
        return [...state];
      }

      return [action.payload, ...state];
    }

    case "DELETE_BOTS":
      return state.filter((q) => q.id !== action.payload);

    case "RESET":
      return [];

    default:
      return state;
  }
};

const Bots = () => {
  const classes = useStyles();

  const history = useHistory();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("id");

  const [botsquerry, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);

  const [botsModalOpen, setBotsModalOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  // ============================================================
  // CARGAR BOT QUERIES
  // ============================================================

  useEffect(() => {
    if (!id) {
      return;
    }

    const loadBotsQuerry = async () => {
      setLoading(true);

      try {
        const { data } = await api.get("/botsquerry", {
          params: { id },
        });

        dispatch({
          type: "LOAD_BOTS",
          payload: Array.isArray(data) ? data : [],
        });
      } catch (err) {
        console.error("[BotsQuerry] Error cargando queries:", err);
        toastError(err);
      } finally {
        setLoading(false);
      }
    };

    loadBotsQuerry();
  }, [id]);

  // ============================================================
  // SOCKET REALTIME
  // ============================================================

  useEffect(() => {
    const socket = openSocket();

    socket.on("botsquerry", (data) => {
      if (["update", "create"].includes(data.action)) {
        dispatch({
          type: "UPDATE_BOTS",
          payload: data.botsquerry,
        });
      }

      if (data.action === "delete") {
        dispatch({
          type: "DELETE_BOTS",
          payload: data.botId,
        });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  // ============================================================
  // MODAL CREAR
  // ============================================================

  const handleOpenQueueModal = () => {
    setSelectedQueue(null);
    setBotsModalOpen(true);
  };

  const handleCloseQueueModal = () => {
    setBotsModalOpen(false);
    setSelectedQueue(null);
  };

  // ============================================================
  // MODAL EDITAR
  // ============================================================

  const handleEditQueue = (botsquerry) => {
    setSelectedQueue(botsquerry);
    setBotsModalOpen(true);
  };

  // ============================================================
  // CONFIRMACION ELIMINAR
  // ============================================================

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedQueue(null);
  };

  // ============================================================
  // ELIMINAR QUERY
  // ============================================================

  const handleDeleteQueue = async (botId) => {
    try {
      await api.delete(`/botsquerry/${botId}`);

      toast.success("Query eliminada correctamente");

      setSelectedQueue(null);
      setConfirmModalOpen(false);
    } catch (err) {
      toastError(err);
    }
  };

  // ============================================================
  // VALIDAR ID
  // ============================================================

  if (!id) {
    return (
      <MainContainer>
        <MainHeader>
          <Title>Bot Queries</Title>
        </MainHeader>

        <Paper className={classes.mainPaper} variant="outlined">
          <Typography align="center">
            No se proporcionó el ID del bot.
          </Typography>
        </Paper>
      </MainContainer>
    );
  }

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          selectedQueue
            ? `Eliminar query "${selectedQueue.queries}"?`
            : "Eliminar query?"
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeleteQueue(selectedQueue.id)}
      >
        ¿Estás seguro de que deseas eliminar esta query?
      </ConfirmationModal>

      <BotQueriesModal
        open={botsModalOpen}
        onClose={handleCloseQueueModal}
        botId={selectedQueue?.id}
        id={id}
      />

      <MainHeader>
  <Title>Bot Queries</Title>

  <MainHeaderButtonsWrapper>
    <Button
      variant="outlined"
      color="secondary"
      onClick={() => history.goBack()}
    >
      ← Regresar
    </Button>

    <Button
      variant="contained"
      color="primary"
      onClick={handleOpenQueueModal}
    >
      Agregar Query
    </Button>
  </MainHeaderButtonsWrapper>
</MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">
                Entrada del usuario
              </TableCell>

              <TableCell align="center">
                Respuesta
              </TableCell>

              <TableCell align="center">
                Requiere
              </TableCell>

              <TableCell align="center">
                Acciones
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {botsquerry.map((bot) => (
              <TableRow key={bot.id}>
                <TableCell align="center">
                  {bot.queries}
                </TableCell>

                <TableCell align="center">
                  <div className={classes.customTableCell}>
                    <Typography
                      style={{
                        width: 300,
                        textAlign: "center",
                      }}
                      noWrap
                      variant="body2"
                    >
                      {bot.replyMessage}
                    </Typography>
                  </div>
                </TableCell>

                <TableCell align="center">
                  {bot.requeries || "-"}
                </TableCell>

                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => handleEditQueue(bot)}
                  >
                    <Edit />
                  </IconButton>

                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedQueue(bot);
                      setConfirmModalOpen(true);
                    }}
                  >
                    <DeleteOutline />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}

            {loading && <TableRowSkeleton columns={4} />}

            {!loading && botsquerry.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No hay queries configuradas para este bot.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Bots;